import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { useTable } from '@tanstack/react-table';
import type { DashboardEarnPositionsFilters as DashboardFilters } from '@/helpers/filter/table';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client/useTableSettings';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { useDashboardEarnPositionsTableData } from '@/hooks/positions/useDashboardEarnPositionsTableData';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { useTableFiltersHandler } from '../../hooks/useTableFiltersHandler';
import { COLUMN_LABELS, columnsEarnPositions, earnPositionsTableFeatures } from '../config/DashboardEarnPositionsTable.columns';
import { EARN_POSITIONS_DEFAULT_TABLE_SETTINGS, EARN_POSITIONS_STORAGE_KEY } from '../config/DashboardEarnPositionsTable.config';
import { useDashboardEarnPositionsFilters } from './useDashboardEarnPositionsFilters';
import { useDashboardEarnPositionsTableRows } from './useDashboardEarnPositionsTableRows';
export const useDashboardEarnPositionsTable = () => {
  const $ = _c(33);
  const {
    address: accountAddress
  } = useViewedAccountAddress();
  let t0;
  if ($[0] !== accountAddress) {
    t0 = {
      pageKey: PageKey.Dashboard,
      tableSettingsObjectKey: EARN_POSITIONS_STORAGE_KEY,
      defaultTableSettings: EARN_POSITIONS_DEFAULT_TABLE_SETTINGS,
      resetFiltersKey: accountAddress
    };
    $[0] = accountAddress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters
  } = useTableSettings(t0);
  let t1;
  if ($[2] !== accountAddress) {
    t1 = {
      accountAddress
    };
    $[2] = accountAddress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    earnPositions,
    facets,
    partial,
    loading,
    error,
    isNotFound
  } = useDashboardEarnPositionsTableData(t1);
  let t2;
  if ($[4] !== earnPositions) {
    t2 = {
      earnPositions
    };
    $[4] = earnPositions;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const earnPositionsTableRows = useDashboardEarnPositionsTableRows(t2);
  let t3;
  if ($[6] !== columnFilters || $[7] !== earnPositionsTableRows) {
    t3 = {
      items: earnPositionsTableRows,
      columnFilters
    };
    $[6] = columnFilters;
    $[7] = earnPositionsTableRows;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    filters,
    filteredItems
  } = useDashboardEarnPositionsFilters(t3);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      assetIdsFilter: "assetIds",
      chainIdsFilter: "chainIds"
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== setColumnFilters) {
    t5 = {
      setColumnFilters,
      filterMapping: t4
    };
    $[10] = setColumnFilters;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    handleFiltersChange
  } = useTableFiltersHandler(t5);
  let t6;
  if ($[12] !== facets?.chainIds) {
    t6 = facets?.chainIds.map(_temp);
    $[12] = facets?.chainIds;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const positionChainIds = t6;
  let t7;
  if ($[14] !== columnFilters || $[15] !== sorting) {
    t7 = {
      sorting,
      columnFilters
    };
    $[14] = columnFilters;
    $[15] = sorting;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== filteredItems || $[18] !== setSorting || $[19] !== t7) {
    t8 = {
      features: earnPositionsTableFeatures,
      data: filteredItems,
      columns: columnsEarnPositions,
      onSortingChange: setSorting,
      enableSortingRemoval: false,
      sortDescFirst: true,
      state: t7,
      getRowId: _temp2
    };
    $[17] = filteredItems;
    $[18] = setSorting;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const table = useTable(t8);
  let t9;
  if ($[21] !== earnPositions || $[22] !== earnPositionsTableRows || $[23] !== error || $[24] !== facets || $[25] !== filters || $[26] !== handleFiltersChange || $[27] !== isNotFound || $[28] !== loading || $[29] !== partial || $[30] !== positionChainIds || $[31] !== table) {
    t9 = {
      earnPositions,
      earnPositionsTableRows,
      facets,
      partial,
      loading,
      error,
      isNotFound,
      table,
      filters,
      handleFiltersChange,
      positionChainIds,
      columnLabels: COLUMN_LABELS
    };
    $[21] = earnPositions;
    $[22] = earnPositionsTableRows;
    $[23] = error;
    $[24] = facets;
    $[25] = filters;
    $[26] = handleFiltersChange;
    $[27] = isNotFound;
    $[28] = loading;
    $[29] = partial;
    $[30] = positionChainIds;
    $[31] = table;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  return t9;
};
function _temp(facet) {
  return facet.value as ChainId;
}
function _temp2(row) {
  return row.id;
}